import PropTypes from 'prop-types';
import style from './DaySelector.module.scss';

// Day indices follow legacy backend convention:
//   Sunday = 0, Monday = 1, ... Saturday = 6
// Order in the UI is Mon → Sun (Monday-start week, matching merchant
// weekly planning mental model).
const DAYS = [
  { label: 'M', short: 'Mon', name: 'Monday', value: 1 },
  { label: 'T', short: 'Tue', name: 'Tuesday', value: 2 },
  { label: 'W', short: 'Wed', name: 'Wednesday', value: 3 },
  { label: 'T', short: 'Thu', name: 'Thursday', value: 4 },
  { label: 'F', short: 'Fri', name: 'Friday', value: 5 },
  { label: 'S', short: 'Sat', name: 'Saturday', value: 6 },
  { label: 'S', short: 'Sun', name: 'Sunday', value: 0 },
];

const WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKENDS = [0, 6];
const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];

// Compare two arrays of day values as sets (order-independent).
function sameSet(a, b) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every(v => set.has(v));
}

// Build a human-readable summary of the current selection.
// Detects named presets, contiguous ranges (e.g. "Mon–Thu"), and
// falls back to a comma-separated list. Keeps merchants grounded
// in what their campaign will actually do.
function buildSummary(selected) {
  if (!selected.length) return { text: 'No days selected', tone: 'empty' };
  if (sameSet(selected, EVERY_DAY)) return { text: 'Every day', tone: 'ok' };
  if (sameSet(selected, WEEKDAYS)) return { text: 'Weekdays · Mon–Fri', tone: 'ok' };
  if (sameSet(selected, WEEKENDS)) return { text: 'Weekends · Sat & Sun', tone: 'ok' };

  // Order by the UI's Mon-first week so the summary reads naturally
  const ordered = DAYS.map(d => d.value).filter(v => selected.includes(v));

  // Detect a single contiguous run from start → end of the Mon-start week
  const shorts = ordered.map(v => DAYS.find(d => d.value === v).short);
  const isContiguous = ordered.every((v, i) => {
    if (i === 0) return true;
    const prev = DAYS.findIndex(d => d.value === ordered[i - 1]);
    const curr = DAYS.findIndex(d => d.value === v);
    return curr === prev + 1;
  });

  if (isContiguous && shorts.length >= 3) {
    return { text: `${shorts[0]}–${shorts[shorts.length - 1]}`, tone: 'ok' };
  }

  return { text: shorts.join(', '), tone: 'ok' };
}

function DaySelector({ selectedDays, onDaySelected }) {
  // Normalize null/undefined to an empty array. CampaignContext
  // initializes `validDays` as null before the user picks anything,
  // and defaulted function parameters only cover `undefined`.
  const days = Array.isArray(selectedDays) ? selectedDays : [];

  const toggleDay = value => {
    const next = days.includes(value) ? days.filter(d => d !== value) : [...days, value];
    onDaySelected(next);
  };

  const applyPreset = preset => {
    // Tapping a preset that's already active clears the selection —
    // gives users a fast "undo" without a separate clear button.
    if (sameSet(days, preset)) {
      onDaySelected([]);
    } else {
      onDaySelected(preset);
    }
  };

  const isWeekdays = sameSet(days, WEEKDAYS);
  const isWeekends = sameSet(days, WEEKENDS);
  const isEveryDay = sameSet(days, EVERY_DAY);
  const summary = buildSummary(days);

  return (
    <div className={style.daySelector}>
      <div className={style.presets} role="group" aria-label="Quick day selection">
        <button
          type="button"
          className={`${style.preset} ${isWeekdays ? style.presetActive : ''}`}
          onClick={() => applyPreset(WEEKDAYS)}
        >
          Weekdays
        </button>
        <button
          type="button"
          className={`${style.preset} ${isWeekends ? style.presetActive : ''}`}
          onClick={() => applyPreset(WEEKENDS)}
        >
          Weekends
        </button>
        <button
          type="button"
          className={`${style.preset} ${isEveryDay ? style.presetActive : ''}`}
          onClick={() => applyPreset(EVERY_DAY)}
        >
          Every day
        </button>

        <span className={`${style.summary} ${style[`summary-${summary.tone}`]}`}>
          {summary.text}
          {days.length > 0 && summary.tone === 'ok' && (
            <span className={style.summaryCount}>
              {days.length} {days.length === 1 ? 'day' : 'days'}
            </span>
          )}
        </span>
      </div>

      <div className={style.pills} role="group" aria-label="Valid days of the week">
        {DAYS.map(day => {
          const selected = days.includes(day.value);
          return (
            <button
              key={day.name}
              type="button"
              className={`${style.pill} ${selected ? style.pillActive : ''}`}
              aria-pressed={selected}
              aria-label={day.name}
              title={day.name}
              onClick={() => toggleDay(day.value)}
            >
              {day.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

DaySelector.propTypes = {
  // Accepts null/undefined (treated as empty) for the common
  // "not yet selected" case in CampaignContext.
  selectedDays: PropTypes.arrayOf(PropTypes.number),
  onDaySelected: PropTypes.func.isRequired,
};

export default DaySelector;
